'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { cached, fetchConfig, restored } from './config.js';
import { NavContext } from './context.js';
import { readCollapsed, writeCollapsed } from './cookie.js';
import { FALLBACK_CONFIG } from './fallback.js';
import { resolveApps } from './resolve.js';
/**
 * useLayoutEffect on the client, useEffect on the server — the standard isomorphic-layout
 * pattern, and it is load-bearing in BOTH directions here.
 *
 * It cannot be the useState initialiser. town-square server-renders this bar, the server
 * cannot read the viewer's localStorage, and a client first render that disagrees with the
 * server HTML is a hydration mismatch — a tested-clean property of that mount today.
 *
 * It cannot be a plain useEffect either. Passive effects are flushed on a scheduler
 * callback that can land AFTER the browser has painted, which puts the wrong catalog on
 * screen for a frame — precisely the flash this whole change exists to remove. Layout
 * effects run synchronously after commit and before paint, so the state swap and its
 * re-render are both done before anything is shown.
 *
 * Picking the hook by `typeof window` keeps React from warning that useLayoutEffect does
 * nothing on the server. Hydration builds the client fiber tree fresh, so the two paths
 * never have to agree on which effect flavour occupied the slot.
 */
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;
function NavProvider({ configUrl, activeAppId, onAppClick, fallbackConfig, initialCollapsed, children, }) {
    const fallback = fallbackConfig ?? FALLBACK_CONFIG;
    // Stale-while-revalidate: paint the last good payload (or the embedded fallback)
    // synchronously, then swap in the fetched one. First paint never waits on the network.
    const [config, setConfig] = useState(() => cached(configUrl) ?? fallback);
    const [collapsed, setCollapsedState] = useState(() => initialCollapsed ?? readCollapsed() ?? false);
    // Before the first paint, upgrade the compiled-in fallback to the last payload this
    // browser actually saw. `cached` wins when it is populated: an in-memory hit means a
    // fetch already landed in THIS page session, which is strictly fresher than the store.
    useBeforePaint(() => {
        if (cached(configUrl)) {
            return;
        }
        const stored = restored();
        if (!stored) {
            return;
        }
        setConfig(stored);
    }, [configUrl]);
    useEffect(() => {
        let live = true;
        void fetchConfig(configUrl).then((next) => {
            if (!live || !next) {
                return;
            }
            setConfig(next);
        });
        return () => {
            live = false;
        };
    }, [configUrl]);
    const setCollapsed = useCallback((next) => {
        setCollapsedState(next);
        writeCollapsed(next);
    }, []);
    const toggle = useCallback(() => {
        setCollapsedState((current) => {
            writeCollapsed(!current);
            return !current;
        });
    }, []);
    const apps = useMemo(() => resolveApps(config, activeAppId, fallback), [config, activeAppId, fallback]);
    const activeApp = useMemo(() => apps.find((item) => item.active), [apps]);
    const activate = useCallback((entry) => {
        onAppClick?.({ app: entry.app, activeAppId, collapsed });
    }, [onAppClick, activeAppId, collapsed]);
    const value = useMemo(() => ({ config, activeAppId, apps, activeApp, collapsed, setCollapsed, toggle, activate }), [config, activeAppId, apps, activeApp, collapsed, setCollapsed, toggle, activate]);
    return _jsx(NavContext.Provider, { value: value, children: children });
}
export { NavProvider };
//# sourceMappingURL=provider.js.map